import { LoadingState, PageContainer } from '@osteopro/ui';
import { lazy, Suspense, type ReactNode } from 'react';
import { Redirect, useLocation } from 'wouter';
import { useEntitlements } from '../../hooks/api/use-plans.js';
import { useSuspension } from '../../hooks/use-suspension.js';
import { useAuth } from '../../lib/auth-provider.js';
import {
  isPlatformOnly,
  isPlatformOnlyPath,
  isPlatformStaff,
  PLATFORM_HOME_PATH,
} from '../../lib/platform-role.js';
import { isOpenWhileBlocked } from '../../lib/subscription-block.js';
import { isBlocked } from '../plans/plan-locks.js';
import { ImpersonationBanner, useImpersonationExit } from '../shell/ImpersonationBanner.js';

const EmailUnverifiedPage = lazy(async () => import('../../pages/EmailUnverifiedPage.js'));
const PasswordChangeRequiredPage = lazy(
  async () => import('../../pages/PasswordChangeRequiredPage.js'),
);
const OrganizationSuspendedPage = lazy(
  async () => import('../../pages/OrganizationSuspendedPage.js'),
);
const SubscriptionBlockedPage = lazy(async () => import('../../pages/SubscriptionBlockedPage.js'));

function GateFallback() {
  return (
    <PageContainer data-testid="state-loading">
      <LoadingState type="card" count={2} />
    </PageContainer>
  );
}

/**
 * Les murs de la zone authentifiée, qui court-circuitent tout, coque comprise : quand le
 * serveur refuse toute requête de données, rendre une barre latérale autour n'inviterait qu'à des
 * clics qui échouent.
 *
 * - **Mot de passe temporaire** (compte créé par l'exploitation) : en choisir un autre, ou se
 *   déconnecter. Le serveur refuse tout le reste en `password_change_required`.
 * - **Compte de plateforme sans cabinet** : tout ce qui n'est pas la console ou son propre
 *   compte ramène à la console (`isPlatformOnly`).
 * - **Adresse non confirmée** : l'application entière est fermée. Renvoyer le lien ou se
 *   déconnecter, rien d'autre. Les droits ne sont pas lus : la route répondrait 403.
 * - **Cabinet suspendu** par la plateforme : seule l'aide reste ouverte, c'est la porte que
 *   l'écran désigne.
 * - **Cabinet bloqué** (essai terminé ou abonnement expiré) : la page d'abonnement et le retour
 *   de caisse restent ouverts — voir `isOpenWhileBlocked`. Ces pages s'affichent **dans**
 *   la coque : un lien de la barre latérale vers un écran fermé ramène simplement au mur.
 *
 * L'**équipe de la plateforme** n'est jamais bloquée (décision du 30 septembre 2026) : elle voit
 * l'application normalement, avec un bandeau qui lui dit ce que voient les membres.
 *
 * L'adresse non confirmée passe avant tout : il n'y a pas encore d'application à montrer. La
 * suspension passe ensuite, avant le blocage d'abonnement : un cabinet suspendu ne se rouvre pas
 * en souscrivant.
 *
 * Tant que les droits ne sont pas chargés, on laisse passer : on ne bloque pas sur une ignorance,
 * et le serveur refuse de son côté.
 */
export function AccessGate({ children }: { children: ReactNode }) {
  const suspension = useSuspension();
  const { user, isAuthenticated, impersonation, organizations } = useAuth();
  const emailVerified = user?.emailVerified === true;
  const platformOnly = isPlatformOnly({
    platformRole: user?.platformRole,
    organizationCount: organizations.length,
    impersonating: impersonation !== null,
  });
  const entitlements = useEntitlements({ enabled: emailVerified && !platformOnly });
  const [location] = useLocation();
  useImpersonationExit();

  // Mot de passe temporaire : le drapeau est celui de la personne connectée. Pendant une séance,
  // `user` est le compte regardé, dont le drapeau ne concerne pas l'administrateur.
  if (isAuthenticated && user?.mustChangePassword === true && impersonation === null) {
    return <Wall page={<PasswordChangeRequiredPage />} />;
  }

  if (isAuthenticated && user !== null && !emailVerified) {
    return <Wall page={<EmailUnverifiedPage />} />;
  }

  if (platformOnly && !isPlatformOnlyPath(location)) {
    return <Redirect to={PLATFORM_HOME_PATH} replace />;
  }

  if (suspension !== null && !location.startsWith('/aide')) {
    return <Wall page={<OrganizationSuspendedPage />} />;
  }

  // L'équipe de la plateforme n'est jamais bloquée : elle voit l'application, avec un bandeau
  // discret dans la coque (`StaffBlockedBanner`). Le serveur la laisse passer de son côté.
  const data = entitlements.data;
  const staff = isPlatformStaff(user?.platformRole);
  if (data !== undefined && isBlocked(data) && !staff && !isOpenWhileBlocked(location)) {
    return <Wall page={<SubscriptionBlockedPage entitlements={data} />} />;
  }

  return <>{children}</>;
}

/**
 * Un mur, et au-dessus le bandeau d'une séance « Voir comme » s'il y en a une : le mur du compte
 * regardé ne doit pas enfermer l'administrateur.
 */
function Wall({ page }: { page: ReactNode }) {
  return (
    <>
      <ImpersonationBanner />
      <Suspense fallback={<GateFallback />}>{page}</Suspense>
    </>
  );
}
